/** A short, skippable introduction to the current Lyrixsa product catalogue. */
import { ArrowRight, Check, Rocket } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import PlanCard, { ADDON_BUY_DISABLED_REASON } from '../components/account/PlanCard';
import { Btn } from '../components/ui';
import { JVZOO_PRODUCT_URLS } from '../config';
import { FRONTEND_PRODUCT } from '../data/products';
import { resolveUpgradeOffers } from '../data/upgradeOffers';
import { firstNameOf } from '../lib/attribution';
import { openExternalUrl } from '../lib/externalNavigation';
import { selectProfileName, useAuthStore } from '../stores/authStore';
import { c } from '../styles/theme';
import type { CheckoutProductId } from '../types/domain';
import { paths } from './paths';

export default function Onboarding() {
  const navigate = useNavigate();
  const profileName = useAuthStore(selectProfileName);
  const checkoutOwnership = useAuthStore((state) => state.checkoutOwnership);
  const firstName = firstNameOf(profileName);

  const offers = resolveUpgradeOffers({ checkoutOwnership }).filter(
    (offer) => offer.checkoutId !== 'frontend' && offer.visible
  );
  const ownedCount = offers.filter((offer) => offer.owned).length;

  function finish(): void {
    navigate(paths.dashboard, { replace: true });
  }

  function openCheckout(productId: CheckoutProductId): void {
    openExternalUrl(JVZOO_PRODUCT_URLS[productId]);
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Rocket size={16} color={c.accent} />
        <h1
          style={{
            fontFamily: 'Space Grotesk',
            fontWeight: 600,
            fontSize: 18,
            margin: 0,
            color: c.text1,
          }}
        >
          {firstName ? `You're in, ${firstName}` : `You're in`}
        </h1>
      </div>
      <p style={{ fontSize: 12.5, color: c.text2, margin: '0 0 16px', lineHeight: 1.6 }}>
        Your account is ready and {FRONTEND_PRODUCT.name} is included - {FRONTEND_PRODUCT.credits}{' '}
        starter credits after verification. Add an upgrade now or continue to the app.
      </p>

      <div
        className="lyx-plan-status"
        style={{ background: c.accentSoft, border: `1px solid ${c.accentBorder}` }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <Check size={15} color={c.accent} style={{ flexShrink: 0 }} />
          <p style={{ fontSize: 12.5, margin: 0, color: c.accent }}>
            {FRONTEND_PRODUCT.name} - included
          </p>
        </div>
        {ownedCount > 0 ? (
          <span style={{ fontSize: 11, color: c.accent, fontWeight: 500, flexShrink: 0 }}>
            {ownedCount} added
          </span>
        ) : null}
      </div>

      <h2
        style={{
          fontSize: 12,
          color: c.text2,
          fontWeight: 400,
          fontFamily: 'Inter',
          margin: '0 0 8px',
        }}
      >
        Optional upgrades
      </h2>

      <div className="lyx-plan-grid lyx-plan-grid--two-col" style={{ marginBottom: 20 }}>
        {offers.map((offer) => (
          <PlanCard
            key={offer.checkoutId}
            product={offer.product}
            owned={offer.owned}
            recommended={offer.recommended}
            recommendedLabel={offer.recommendedLabel}
            highlyRecommended={offer.highlyRecommended}
            supportingBadgeLabel={offer.supportingBadgeLabel}
            highlightVariant={offer.highlightVariant}
            layout={offer.layout}
            onBuy={() => openCheckout(offer.checkoutId)}
            buyHref={JVZOO_PRODUCT_URLS[offer.checkoutId]}
            buyLabel="Choose"
            buyDisabledReason={
              offer.layout === 'addon-inline' && !offer.owned
                ? ADDON_BUY_DISABLED_REASON
                : undefined
            }
            compact
          />
        ))}
      </div>

      <div
        className="lyx-onboarding-actions"
        style={{ background: c.bg, borderTop: `1px solid ${c.border}` }}
      >
        <Btn onClick={finish} style={{ flex: 1 }}>
          {ownedCount > 0 ? 'Continue to Lyrixsa AI' : 'Continue with what I have'}
          <ArrowRight size={13} />
        </Btn>
        <Btn variant="ghost" onClick={finish} aria-label="Skip plan selection for now">
          Skip for now
        </Btn>
      </div>
    </div>
  );
}
